<template>
    <div class="scanner-test-page">
        <div class="container-fluid">
            <!-- Page Header -->
            <div class="page-header">
                <div class="row align-items-center">
                    <div class="col">
                        <h1 class="h3 mb-0">Scanner Test Center</h1>
                        <p class="text-muted mb-0">Test and troubleshoot wireless scanner functionality</p>
                    </div>
                    <div class="col-auto">
                        <button class="btn btn-outline-primary" @click="refreshPage">
                            <i class="material-icons">refresh</i>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            <!-- Scanner Status Cards -->
            <div class="row mb-4">
                <div class="col-md-4">
                    <div class="card scanner-status-card">
                        <div class="card-body">
                            <div class="d-flex align-items-center">
                                <div class="scanner-icon-container" :class="scannerStatusClass">
                                    <i class="material-icons">{{ scannerIcon }}</i>
                                </div>
                                <div class="ml-3">
                                    <h6 class="card-title mb-1">Scanner Status</h6>
                                    <p class="card-text mb-0" :class="`text-${scannerStatusColor}`">
                                        {{ scannerStatus }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-4">
                    <div class="card">
                        <div class="card-body">
                            <div class="d-flex align-items-center">
                                <div class="icon-container bg-info">
                                    <i class="material-icons text-white">network_check</i>
                                </div>
                                <div class="ml-3">
                                    <h6 class="card-title mb-1">Connection</h6>
                                    <p class="card-text mb-0">
                                        {{ scannerConnected ? 'Connected' : 'Disconnected' }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div class="col-md-4">
                    <div class="card">
                        <div class="card-body">
                            <div class="d-flex align-items-center">
                                <div class="icon-container bg-warning">
                                    <i class="material-icons text-white">history</i>
                                </div>
                                <div class="ml-3">
                                    <h6 class="card-title mb-1">Total Scans</h6>
                                    <p class="card-text mb-0">{{ scanHistory.length }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Content Row -->
            <div class="row">
                <!-- Left Column: Scanner Controls -->
                <div class="col-lg-6">
                    <div class="card mb-4">
                        <div class="card-header">
                            <h5 class="card-title mb-0">Scanner Controls</h5>
                        </div>
                        <div class="card-body">
                            <div class="scanner-controls">
                                <div class="row">
                                    <div class="col-6">
                                        <button 
                                            class="btn btn-block"
                                            :class="scannerActive ? 'btn-danger' : 'btn-success'"
                                            @click="toggleScanner"
                                            :disabled="!scannerConnected"
                                        >
                                            <i class="material-icons mr-2">
                                                {{ scannerActive ? 'stop' : 'play_arrow' }}
                                            </i>
                                            {{ scannerActive ? 'Stop Scanner' : 'Start Scanner' }}
                                        </button>
                                    </div>
                                    <div class="col-6">
                                        <button 
                                            class="btn btn-outline-info btn-block"
                                            @click="testScan"
                                            :disabled="!scannerActive"
                                        >
                                            <i class="material-icons mr-2">science</i>
                                            Test Scan
                                        </button>
                                    </div>
                                </div>
                                
                                <div class="row mt-3">
                                    <div class="col-6">
                                        <button 
                                            class="btn btn-outline-primary btn-block"
                                            @click="reconnectScanner"
                                        >
                                            <i class="material-icons mr-2">refresh</i>
                                            Reconnect
                                        </button>
                                    </div>
                                    <div class="col-6">
                                        <button 
                                            class="btn btn-outline-warning btn-block"
                                            @click="clearHistory"
                                            :disabled="scanHistory.length === 0"
                                        >
                                            <i class="material-icons mr-2">clear_all</i>
                                            Clear History
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Manual Test Input -->
                    <div class="card">
                        <div class="card-header">
                            <h5 class="card-title mb-0">Manual Test Input</h5>
                        </div>
                        <div class="card-body">
                            <div class="input-group">
                                <input 
                                    v-model="manualTestValue"
                                    type="text" 
                                    class="form-control" 
                                    placeholder="Enter test barcode value"
                                    @keyup.enter="runManualTest"
                                >
                                <div class="input-group-append">
                                    <button 
                                        class="btn btn-primary" 
                                        type="button"
                                        @click="runManualTest"
                                        :disabled="!manualTestValue.trim() || !scannerActive"
                                    >
                                        Simulate Scan
                                    </button>
                                </div>
                            </div>
                            <small class="text-muted">
                                This will simulate a scan event with your custom value
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Right Column: Real-time Display -->
                <div class="col-lg-6">
                    <!-- Last Scan Display -->
                    <div class="card mb-4">
                        <div class="card-header">
                            <h5 class="card-title mb-0">Last Scan Result</h5>
                        </div>
                        <div class="card-body">
                            <div class="last-scan-display">
                                <div v-if="lastScanData" class="scan-result">
                                    <div class="scan-value">
                                        <code>{{ lastScanData }}</code>
                                    </div>
                                    <div class="scan-type">
                                        <span class="badge" :class="getScanTypeBadgeClass(lastScanData)">
                                            {{ getScanType(lastScanData) }}
                                        </span>
                                    </div>
                                    <div class="scan-timestamp">
                                        <small class="text-muted">
                                            <i class="material-icons">access_time</i>
                                            {{ lastScanTime }}
                                        </small>
                                    </div>
                                </div>
                                <div v-else class="no-scan">
                                    <i class="material-icons">scanner</i>
                                    <p class="mb-0">No scans yet</p>
                                    <small class="text-muted">Scan a barcode to see results here</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Scan History -->
                    <div class="card">
                        <div class="card-header d-flex justify-content-between align-items-center">
                            <h5 class="card-title mb-0">Scan History</h5>
                            <span class="badge badge-secondary">{{ scanHistory.length }}</span>
                        </div>
                        <div class="card-body p-0">
                            <div class="scan-history-list">
                                <div 
                                    v-for="(scan, index) in scanHistory.slice(0, 10)" 
                                    :key="index"
                                    class="scan-history-item"
                                    :class="{ 'latest': index === 0 }"
                                >
                                    <div class="scan-content">
                                        <code class="scan-value">{{ scan }}</code>
                                        <span class="badge badge-sm ml-2" :class="getScanTypeBadgeClass(scan)">
                                            {{ getScanType(scan) }}
                                        </span>
                                    </div>
                                    <small class="scan-index">#{{ scanHistory.length - index }}</small>
                                </div>
                                <div v-if="scanHistory.length === 0" class="empty-history">
                                    <i class="material-icons">history</i>
                                    <p class="mb-0">No scan history</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ScannerMixin from '@/mixins/scanner-mixin';

export default {
    name: 'ScannerTest',
    mixins: [ScannerMixin],
    
    data() {
        return {
            manualTestValue: '',
            lastScanTime: null
        };
    },

    methods: {
        // Override scanner event handlers for testing
        onScanReceived(scanData) {
            console.log('Test page received scan:', scanData);
            this.lastScanTime = new Date().toLocaleTimeString();
            
            // Show a success message
            if (this.$toast) {
                this.$toast.success(`Scan received: ${scanData}`);
            }
        },

        async handleProductScan(scanData) {
            console.log('Product scan test:', scanData);
        },

        async handleInvoiceScan(scanData) {
            console.log('Invoice scan test:', scanData);
        },

        async handleCustomerScan(scanData) {
            console.log('Customer scan test:', scanData);
        },

        async handleGenericScan(scanData) {
            console.log('Generic scan test:', scanData);
        },

        // Test methods
        testScan() {
            const testValue = `TEST_${Date.now()}`;
            this.$scanner.testScan(testValue);
        },

        runManualTest() {
            if (this.manualTestValue.trim()) {
                this.$scanner.testScan(this.manualTestValue.trim());
                this.manualTestValue = '';
            }
        },

        toggleScanner() {
            if (this.scannerActive) {
                this.stopScanner();
            } else {
                this.startScanner();
            }
        },

        reconnectScanner() {
            this.$scanner.reconnect();
        },

        clearHistory() {
            this.$scanner.clearHistory();
        },

        refreshPage() {
            this.$router.go(0);
        },

        // Utility methods
        getScanType(scanData) {
            if (!scanData) return 'Unknown';
            
            // Check for UPC/EAN patterns
            if (/^\d{12,13}$/.test(scanData)) {
                return 'Product';
            }
            
            // Check for invoice pattern
            if (/^INV-?/i.test(scanData)) {
                return 'Invoice';
            }
            
            // Check for customer pattern
            if (/^CUST-?/i.test(scanData)) {
                return 'Customer';
            }
            
            // Check for URL pattern
            if (/^https?:\/\//.test(scanData)) {
                return 'URL';
            }
            
            return 'Generic';
        },

        getScanTypeBadgeClass(scanData) {
            const type = this.getScanType(scanData);
            switch (type) {
                case 'Product': return 'badge-success';
                case 'Invoice': return 'badge-primary';
                case 'Customer': return 'badge-info';
                case 'URL': return 'badge-warning';
                default: return 'badge-secondary';
            }
        }
    },

    computed: {
        scannerStatusColor() {
            const status = this.scannerStatus.toLowerCase();
            if (status.includes('connected') || status.includes('ready')) {
                return 'success';
            } else if (status.includes('scanning') || status.includes('active')) {
                return 'warning';
            } else {
                return 'danger';
            }
        }
    },

    mounted() {
        // Auto-start scanner for testing
        setTimeout(() => {
            if (this.scannerConnected && !this.scannerActive) {
                this.startScanner();
            }
        }, 1000);
    }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";
.dark .scanner-test-page { background-color: $backgroundColorDark; }
.dark .page-header { background: $darkCardBg; border-color: $darkBorderColor; color: $darkTextColor; }
.dark .last-scan-display .scan-result .scan-value { background: lighten($darkCardBg, 4%); border-color: $darkBorderColor; }
.dark .scan-history-list .scan-history-item { border-bottom-color: $darkBorderColor; }
.dark .scan-history-list .scan-history-item.latest { border-left-color: $successColorDark; }
.dark .no-scan, .dark .scan-history-list .empty-history, .dark .scan-history-list .scan-index { color: rgba($darkTextColor, 0.7); }
.scanner-test-page {
    /* Tokenized page padding for consistency */
    padding: $cardPaddingY $cardPaddingX;
    min-height: 100vh;
    background-color: $backgroundColor;
}

.page-header {
    /* Tokenized header block to match app shell cards */
    margin-bottom: $sectionGap;
    padding: $cardPaddingY $cardPaddingX;
    background: $lightCardBg;
    border-radius: $cardRadius;
    box-shadow: $cardShadow;
}

.scanner-status-card {
    .scanner-icon-container {
        width: 50px;
        height: 50px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.scanner-connected {
            background: linear-gradient(135deg, $successColor 0%, lighten($successColor, 8%) 100%);
        }

        &.scanner-active {
            background: linear-gradient(135deg, $warningColor 0%, lighten($warningColor, 8%) 100%);
            animation: pulse 2s infinite;
        }

        &.scanner-disconnected {
            background: linear-gradient(135deg, rgba($textColor, 0.5) 0%, rgba($textColor, 0.7) 100%);
        }

        .material-icons {
            color: white;
            font-size: 24px;
        }
    }
}

.icon-container {
    width: 50px;
    height: 50px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    .material-icons {
        font-size: 24px;
    }
}

.scanner-controls .btn {
    height: 50px;
    font-weight: 500;
}

.last-scan-display {
    text-align: center;
    padding: 30px 20px;

    .scan-result {
        .scan-value {
            font-size: 18px;
            margin-bottom: 15px;
            padding: 10px;
            background: $lightHoverColor;
            border-radius: 6px;
            border: 2px dashed $lightBorderColor;
        }

        .scan-type {
            margin-bottom: 10px;
        }

        .scan-timestamp {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 5px;

            .material-icons {
                font-size: 16px;
            }
        }
    }

    .no-scan {
        color: rgba($textColor, 0.65);

        .material-icons {
            font-size: 48px;
            margin-bottom: 15px;
            opacity: 0.5;
        }
    }
}

.scan-history-list {
    max-height: 400px;
    overflow-y: auto;

    .scan-history-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 20px;
        border-bottom: 1px solid $lightBorderColor;

        &.latest {
            background: linear-gradient(90deg, rgba(40, 167, 69, 0.1), transparent);
            border-left: 3px solid $successColor;
        }

        .scan-content {
            display: flex;
            align-items: center;
            flex: 1;

            .scan-value {
                font-size: 14px;
            }
        }

        .scan-index {
            color: rgba($textColor, 0.6);
            font-weight: 500;
        }
    }

    .empty-history {
        padding: 40px;
        text-align: center;
        color: rgba($textColor, 0.6);

        .material-icons {
            font-size: 36px;
            margin-bottom: 10px;
            opacity: 0.5;
        }
    }
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}

.badge-sm {
    font-size: 0.75em;
    padding: 0.25em 0.5em;
}
</style> 
